import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { getMediaCategory } from '~/components/ai-elements/attachments';
import { apiClient } from '~/lib/apiClient';
import { useAuthStore } from '~/stores/useAuthStore';
import { useActiveTeam } from '~/stores/useTeamStore';
import { useToast } from './use-toast';

export interface Chat {
  id: number;
  title: string;
}

interface ChatsPage {
  chats: Chat[];
  has_more: boolean;
  next_cursor: number | null;
}

export const CHATS_QUERY_KEY = 'chats';
const PAGE_LIMIT = 20;

export function useChats() {
  const activeTeam = useActiveTeam();

  const query = useInfiniteQuery<ChatsPage>({
    queryKey: [CHATS_QUERY_KEY, activeTeam?.id],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { limit: PAGE_LIMIT };
      if (pageParam) {
        params.before_id = pageParam as number;
      }
      const data = await apiClient.get<ChatsPage>('/ai/chat', params);
      return data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.next_cursor == null) return undefined;
      return lastPage.next_cursor;
    },
    enabled: !!activeTeam?.id,
    staleTime: 1000 * 60 * 5,
  });

  const data: Chat[] = query.data?.pages.flatMap((p) => p.chats) ?? [];

  return {
    ...query,
    data,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
}

export function useAddChat() {
  return useMutation({
    mutationFn: async () => {
      return await apiClient.post<Chat>('/ai/chat');
    },
  });
}

export function useUpdateChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title }: { id: number; title: string }) => {
      return await apiClient.patch<Chat>(`/ai/chat/${id}`, { title });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHATS_QUERY_KEY] });
    },
  });
}

export function useDeleteChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/ai/chat/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHATS_QUERY_KEY] });
    },
  });
}

export type ProcessingFile = {
  id: string;
  filename: string;
  progress: number;
  statusText: string;
};

export type UploadedFilePart = {
  type: 'file';
  url: string;
  mediaType: string;
  filename: string;
  key: string;
  indexed?: boolean;
};

const FILE_UPLOAD_STATUS_MAP: Record<string, string> = {
  'file.upload.completed': 'Uploaded...',
  'file.parsing.started': 'Reading file...',
  'file.parsing.completed': 'File read...',
  'file.chunking.completed': 'Analyzing...',
  'file.embedding.started': 'Analyzing...',
  'file.embedding.completed': 'Almost done...',
};

export function useChatFileUpload() {
  const [processingFiles, setProcessingFiles] = useState<ProcessingFile[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<UploadedFilePart[]>([]);
  const { toast } = useToast();
  const token = useAuthStore((state) => state.token);
  const activeTeam = useActiveTeam();
  const baseUrl = import.meta.env.VITE_BACKEND_URL;

  const uploadFile = async (
    file: File,
    chatId: string,
  ): Promise<UploadedFilePart | null> => {
    const id = crypto.randomUUID();
    setProcessingFiles((prev) => [
      ...prev,
      { id, filename: file.name, progress: 0, statusText: 'Uploading...' },
    ]);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(
        `${baseUrl}/api/ai/chat/upload?chat_id=${chatId}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
            team_id: activeTeam?.id?.toString() || '',
          },
        },
      );

      if (!res.ok || !res.body) throw new Error('Upload failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            setProcessingFiles((prev) =>
              prev.map((f) =>
                f.id === id
                  ? {
                      ...f,
                      progress: event.progress ?? f.progress,
                      statusText:
                        FILE_UPLOAD_STATUS_MAP[event.event] ?? f.statusText,
                    }
                  : f,
              ),
            );

            if (event.event === 'file.processing.completed') {
              setProcessingFiles((prev) => prev.filter((f) => f.id !== id));
              const uploaded: UploadedFilePart = {
                type: 'file' as const,
                mediaType: file.type,
                url: event.url,
                filename: event.filename ?? file.name,
                key: event.key,
                indexed: true,
              };
              setAttachedFiles((prev) => [...prev, uploaded]);
              return uploaded;
            }

            if (event.event === 'file.processing.failed') {
              setProcessingFiles((prev) => prev.filter((f) => f.id !== id));
              toast({
                title: 'Upload failed',
                description: event.message ?? `Failed to process ${file.name}`,
                variant: 'destructive',
              });
              return null;
            }
          } catch {}
        }
      }

      setProcessingFiles((prev) => prev.filter((f) => f.id !== id));
      toast({
        title: 'Upload failed',
        description: `Processing incomplete for ${file.name}`,
        variant: 'destructive',
      });
      return null;
    } catch {
      setProcessingFiles((prev) => prev.filter((f) => f.id !== id));
      toast({
        title: 'Upload failed',
        description: `Failed to upload ${file.name}`,
        variant: 'destructive',
      });
      return null;
    }
  };

  const removeFile = async (index: number): Promise<void> => {
    const fileToRemove = attachedFiles[index];
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileToRemove?.key) {
      try {
        await apiClient.delete(
          `/ai/chat/upload?key=${encodeURIComponent(fileToRemove.key)}`,
        );
      } catch {
        setAttachedFiles((prev) => {
          const next = [...prev];
          next.splice(index, 0, fileToRemove);
          return next;
        });
        toast({ title: 'Failed to remove file', variant: 'destructive' });
      }
    }
  };

  const clearFiles = () => setAttachedFiles([]);

  return { processingFiles, attachedFiles, uploadFile, removeFile, clearFiles };
}

function toFileData(messageId: string, p: any, index: number) {
  return {
    id: `${messageId}-file-${index}`,
    type: 'file' as const,
    url: p.url,
    mediaType: p.mediaType,
    filename: p.filename,
  };
}

export function useMessageParts(message: any) {
  return useMemo(() => {
    const parts = (message.parts || []) as any[];
    let hasText = false;
    let hasTools = false;
    const imageParts: any[] = [];
    const docParts: any[] = [];
    const textChunks: string[] = [];

    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (p.type === 'text' && p.text) {
        hasText = true;
        textChunks.push(p.text);
      } else if (p.type === 'file' && p.url && message.role === 'user') {
        const file = toFileData(message.id, p, i);
        (getMediaCategory(file) === 'image' ? imageParts : docParts).push(p);
      } else if (p.type === 'dynamic-tool') {
        hasTools = true;
      }
    }

    return {
      hasText,
      hasTools,
      imageParts,
      docParts,
      copyText: textChunks.join('\n\n'),
    };
  }, [message.parts, message.role, message.id]);
}
